import { type Row, type Table as TableType, flexRender } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { TableHeaderRow } from "./table-header";

const NARROW_COLUMNS = ["actions", "index", "select"];

/** Kengligi ko'rsatilmagan ustun uchun ulush. */
const DEFAULT_COLUMN_WIDTH = 160;

interface ReactTableProps<TData> {
  table: TableType<TData>;
  isLoading?: boolean;
  /** Bo'sh ro'yxat matni. Xato holatida sabab shu yerdan uzatiladi. */
  emptyTitle?: string;
  emptyDescription?: string;
  skeletonRows?: number;
  pagination?: ReactNode;
  onRowClick?: (row: Row<TData>) => void;
  className?: string;
  rowClassName?: string | ((row: Row<TData>) => string);
}

/**
 * Ro'yxat jadvali.
 *
 * Sahifa bergan balandlikni to'liq egallaydi — viewport'dan chegirma
 * hisoblamaydi (`100vh - 280px` kabi qiymat faqat bitta masshtabda va
 * bitta ekranda to'g'ri chiqadi). Sahifa ildizi `h-full`, bu yerdagi
 * `flex-1` esa jadvalni aynan pastki chegaraga olib boradi; `min-h-0`
 * tanaga qisqarib aylanish imkonini beradi, aks holda sahifalash
 * ko'rinmaydigan joyga surilib ketadi.
 */
export function ReactTable<TData>({
  table,
  isLoading = false,
  emptyTitle,
  emptyDescription,
  skeletonRows = 12,
  pagination,
  onRowClick,
  className,
  rowClassName,
}: Readonly<ReactTableProps<TData>>) {
  const { t } = useTranslation();
  const visibleColumns = table.getVisibleLeafColumns();
  const columnCount = visibleColumns.length;

  /**
   * Jadvalning eng kichik kengligi — ustun kengliklari yig'indisi.
   *
   * `table-fixed` + `w-full` da brauzer ustunlarni konteynerga
   * SIQADI: 1200 pikselli jadval 900 pikselli oynada har bir ustunni
   * proporsional toraytiradi, ichidagi nishon esa toraymaydi va
   * qo'shni ustun ustiga chiqib ketadi. `minWidth` shu siqilishni
   * to'xtatadi — o'rniga konteyner aylantiriladi.
   *
   * Kengligi berilmagan ustun ham nolga tushmasin: unga standart
   * ulush beriladi.
   */
  const minWidth = visibleColumns.reduce(
    (sum, column) => sum + (column.columnDef.meta?.width ?? DEFAULT_COLUMN_WIDTH),
    0,
  );

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: skeletonRows }, (_, rowIndex) => (
        <TableRow key={`skeleton-${rowIndex}`}>
          {Array.from({ length: columnCount }, (_, cellIndex) => (
            <TableCell key={`skeleton-${rowIndex}-${cellIndex}`} className="h-8 border px-2 py-1">
              <Skeleton className="h-4 w-full rounded-sm" />
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (table.getRowModel().rows.length === 0) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={columnCount} className="h-64 text-center">
            <p className="text-sm font-medium">{emptyTitle ?? t("table.noResults")}</p>
            {emptyDescription && (
              <p className="text-muted-foreground mx-auto mt-1.5 max-w-md text-sm">
                {emptyDescription}
              </p>
            )}
          </TableCell>
        </TableRow>
      );
    }

    return table.getRowModel().rows.map(row => (
      <TableRow
        key={row.id}
        data-state={row.getIsSelected() && "selected"}
        className={cn(
          // Yopishgan hujayra `bg-inherit` oladi, ya'ni qatorning foni
          // TO'LIQ shaffofmas bo'lishi shart. Standart `hover:bg-muted/50`
          // yarim shaffof — sichqoncha ustida turganda uning ostidan
          // aylantirib yuborilgan ustunlar ko'rinib qolardi.
          "bg-background hover:bg-muted",
          typeof rowClassName === "function" ? rowClassName(row) : rowClassName,
          onRowClick && "cursor-pointer",
        )}
        tabIndex={onRowClick ? 0 : undefined}
        onClick={() => onRowClick?.(row)}
        onKeyDown={event => {
          if (onRowClick && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onRowClick(row);
          }
        }}
      >
        {row.getVisibleCells().map(cell => {
          const meta = cell.column.columnDef.meta;
          const value = cell.getValue();
          const content = flexRender(cell.column.columnDef.cell, cell.getContext());

          return (
            <TableCell
              key={cell.id}
              className={cn(
                // `overflow-hidden` — kengligidan katta nishon yoki uzun
                // matn qo'shni ustunning ustiga emas, o'z hujayrasining
                // ichida kesiladi.
                "h-8 overflow-hidden border px-2 py-1 align-middle text-xs",
                NARROW_COLUMNS.includes(cell.column.id) && "text-center",
                cell.column.id === "actions" && "sticky right-0 bg-inherit",
              )}
              style={
                meta?.width
                  ? { width: meta.width, minWidth: meta.width, maxWidth: meta.width }
                  : undefined
              }
            >
              {meta?.hasTooltip && (typeof value === "string" || typeof value === "number") ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="truncate">{content}</div>
                  </TooltipTrigger>
                  <TooltipContent>{String(value)}</TooltipContent>
                </Tooltip>
              ) : (
                content
              )}
            </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
      {/* Aylantirish shu yerda: sarlavha `sticky` bo'lib aynan shu
          qutiga yopishadi, sahifaga emas. Kartochka ramkasi YO'Q —
          jadvalning o'z chegaralari allaqachon to'r hosil qiladi, ustiga
          qo'yilgan ikkinchi ramka faqat shovqin qo'shadi. */}
      <div className="thin-scrollbar min-h-0 flex-1 overflow-auto">
        {/* Aylantirishni tashqi quti egallaydi: ichkarida ikkinchi
            scrollport paydo bo'lsa, gorizontal chiziq jadval ostiga
            tushib qoladi va yopishgan sarlavha unga yopishadi. */}
        <Table
          className="w-full table-fixed"
          containerClassName="overflow-visible"
          style={{ minWidth }}
        >
          <TableHeaderRow table={table} />
          <TableBody aria-busy={isLoading}>{renderBody()}</TableBody>
        </Table>
      </div>

      {pagination && (
        <div className="shrink-0">
          <Separator className="mt-4" />
          {pagination}
        </div>
      )}
    </div>
  );
}
